import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import AsyncSelect from "react-select/async";
import { useNavigate } from "react-router-dom";
import { FaExchangeAlt, FaUpload, FaStore, FaMedkit, FaBoxes, FaCheck, FaArrowLeft } from "react-icons/fa";
import { motion } from "framer-motion";
import Swal from "sweetalert2";
import PageCreateHeader from "../../components/PageCreateHeader";

const CreateRebalance = () => {
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");
  const role = (localStorage.getItem("role") || "").toLowerCase();
  const outletFromStorage = localStorage.getItem("id_outlet");
  const navigate = useNavigate();

  const [outletName, setOutletName] = useState("");
  const [type, setType] = useState("out");
  const [obat, setObat] = useState(null);
  const [outletId, setOutletId] = useState(role === "manager" ? Number(outletFromStorage) : "");
  const [outletOptions, setOutletOptions] = useState([]);
  const [expiredOptions, setExpiredOptions] = useState([]);
  const [expiredId, setExpiredId] = useState("");
  const [stock, setStock] = useState("");
  const [message, setMessage] = useState("");
  const [objective, setObjective] = useState("");
  const [refId, setRefId] = useState("");
  const [evidence, setEvidence] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");
  const [distributorOptions, setDistributorOptions] = useState([]);
  const [useDistributor, setUseDistributor] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (["broken", "expired", "outlet"].includes(objective)) {
      setType("out");
    } else if (objective === "rebalance") {
      setType("in");
    }
  }, [objective]);

  useEffect(() => {
    if (role === "manager" && outletFromStorage) {
      axios
        .get(`${process.env.REACT_APP_API_BASE_URL}/v1/outlet`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((res) => {
          const outlet = (res.data.data || []).find((o) => Number(o.id) === Number(outletFromStorage));
          if (outlet) {
            setOutletName(outlet.name);
          }
        })
        .catch((err) => console.error("Gagal mendapatkan nama outlet manager:", err));
    }
  }, [role, outletFromStorage, token]);

  useEffect(() => {
    if (role !== "manager") {
      axios
        .get(`${process.env.REACT_APP_API_BASE_URL}/v1/outlet`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((res) => {
          const options = (res.data.data || []).map((o) => ({
            value: o.id,
            label: o.name,
          }));
          setOutletOptions(options);
        })
        .catch((err) => console.error("Gagal load outlet:", err));
    }
  }, [token, role]);

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/v1/distributor`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        const data = (res.data.data || []).map((d) => ({
          value: d.id,
          label: d.name,
        }));
        setDistributorOptions(data);
      })
      .catch((err) => console.error(err));
  }, [token]);

  useEffect(() => {
    if (!obat || !outletId) return;

    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/v1/drug/stock/${obat.value}/${outletId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        const data = (res.data.data || []).map((item) => ({
          value: item.id,
          label: `${new Date(item.expired_at).toLocaleDateString("id-ID")} - Stok: ${item.stock}`,
          stock: item.stock,
        }));
        setExpiredOptions(data);
      })
      .catch((err) => console.error("Gagal load expired_id:", err));
  }, [obat, outletId, token]);

  const loadObatOptions = async (inputValue) => {
    if (!outletId) return [];

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/v1/drug?id_outlet=${outletId}&search=${inputValue}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      return (res.data.data || []).map((obatItem) => ({
        value: obatItem.id,
        label: obatItem.name,
      }));
    } catch (err) {
      console.error("Error fetching obat:", err);
      return [];
    }
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (!file) return;
    readFile(file);
  };

  const readFile = (file) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      setEvidence(reader.result);
      setPreviewUrl(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!objective || !outletId || !obat || !stock) {
      Swal.fire({
        icon: "warning",
        title: "Form Belum Lengkap",
        text: "Harap lengkapi semua bidang bertanda bintang (*)!",
        customClass: { popup: "rounded-3xl" },
      });
      return;
    }

    const confirm = await Swal.fire({
      title: "Konfirmasi Simpan",
      text: `Yakin ingin membuat rebalance stok (${type.toUpperCase()}) ini?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Ya, Kirim",
      cancelButtonText: "Batal",
      customClass: { popup: "rounded-3xl" },
    });

    if (!confirm.isConfirmed) return;

    setLoadingSubmit(true);

    const payload = {
      type,
      obat_id: obat?.value,
      outlet_id: Number(outletId),
      expired_id: Number(expiredId),
      stock: parseInt(stock),
      message,
      objective,
      ref_id: Number(refId) || 0,
      evidence,
    };

    try {
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/v1/rebalance`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      Swal.fire({
        icon: "success",
        title: "Berhasil!",
        text: "Rebalance stok obat berhasil dibuat.",
        timer: 1500,
        showConfirmButton: false,
        customClass: { popup: "rounded-3xl" },
      });
      resetForm();
      navigate("/rebalance");
    } catch (err) {
      console.error(err);
      Swal.fire({
        icon: "error",
        title: "Gagal",
        text: err.response?.data?.message || "Gagal membuat data rebalance stok.",
        customClass: { popup: "rounded-3xl" },
      });
    } finally {
      setLoadingSubmit(false);
    }
  };

  const resetForm = () => {
    setObat(null);
    setOutletId(role === "manager" ? Number(outletFromStorage) : "");
    setExpiredId("");
    setStock("");
    setMessage("");
    setObjective("");
    setRefId("");
    setEvidence("");
    setPreviewUrl("");
    setUseDistributor(false);
  };

  return (
    <div className="space-y-6 pb-12">
      <PageCreateHeader
        icon={FaExchangeAlt}
        title={`Buat Rebalance (${type === "in" ? "IN - Masuk" : "OUT - Keluar"})`}
        description="Catat penyesuaian mutasi stok obat masuk atau keluar antar cabang outlet"
        breadcrumbs={[
          { label: "Inventory", to: "/rebalance" },
          { label: "Rebalance", to: "/rebalance" },
          { label: `Buat Mutasi (${type.toUpperCase()})` },
        ]}
      />

      <motion.form
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        onSubmit={handleSubmit}
        className="bg-white p-6 md:p-8 rounded-3xl shadow-xl shadow-gray-200/50 border border-gray-100 space-y-6 text-xs"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Outlet Selector */}
          <div className="space-y-1.5">
            <label className="text-gray-700 font-bold uppercase text-[10px] tracking-wider flex items-center gap-1.5">
              <FaStore className="text-emerald-600" /> Outlet Cabang *
            </label>
            {role === "manager" ? (
              <input
                type="text"
                disabled
                value={outletName || `Outlet #${outletFromStorage}`}
                className="w-full p-3 bg-gray-100 border border-gray-200 rounded-2xl text-xs text-gray-700 font-bold"
              />
            ) : (
              <select
                value={outletId}
                onChange={(e) => {
                  setOutletId(e.target.value);
                  setObat(null);
                  setExpiredId("");
                }}
                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
                required
              >
                <option value="">Pilih Outlet Cabang</option>
                {outletOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Objective Selector */}
          <div className="space-y-1.5">
            <label className="text-gray-700 font-bold uppercase text-[10px] tracking-wider flex items-center gap-1.5">
              <FaExchangeAlt className="text-emerald-600" /> Objective / Tujuan Mutasi *
            </label>
            <select
              value={objective}
              onChange={(e) => setObjective(e.target.value)}
              className="w-full p-3 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
              required
            >
              <option value="">Pilih Objective Mutasi</option>
              <option value="broken">Broken (Stok Rusak - OUT)</option>
              <option value="expired">Expired (Stok Kadaluarsa - OUT)</option>
              <option value="outlet">Mutasi Antar Outlet (OUT)</option>
              <option value="rebalance">Rebalance Stok (IN)</option>
            </select>
          </div>

          {/* Obat Selector AsyncSelect */}
          <div className="space-y-1.5">
            <label className="text-gray-700 font-bold uppercase text-[10px] tracking-wider flex items-center gap-1.5">
              <FaMedkit className="text-emerald-600" /> Cari & Pilih Obat Master *
            </label>
            <AsyncSelect
              cacheOptions
              defaultOptions
              loadOptions={loadObatOptions}
              value={obat}
              onChange={(selected) => {
                setObat(selected);
                setExpiredId("");
              }}
              isDisabled={!outletId}
              placeholder={outletId ? "Ketik nama obat..." : "Pilih outlet terlebih dahulu"}
              styles={{
                control: (base) => ({
                  ...base,
                  backgroundColor: "#f9fafb",
                  borderColor: "#e5e7eb",
                  borderRadius: "1rem",
                  padding: "0.2rem",
                  fontSize: "0.75rem",
                  fontWeight: "600",
                }),
              }}
            />
          </div>

          {/* Expired Stock Date Selector */}
          <div className="space-y-1.5">
            <label className="text-gray-700 font-bold uppercase text-[10px] tracking-wider flex items-center gap-1.5">
              <FaBoxes className="text-emerald-600" /> Batch Expired & Stok Obat *
            </label>
            <select
              value={expiredId}
              onChange={(e) => setExpiredId(e.target.value)}
              disabled={!obat}
              className="w-full p-3 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all disabled:opacity-50"
              required
            >
              <option value="">Pilih Batch Expired</option>
              {expiredOptions.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </div>

          {/* Jumlah Stock Input */}
          <div className="space-y-1.5">
            <label className="text-gray-700 font-bold uppercase text-[10px] tracking-wider">
              Jumlah Stok Dimutasi (Qty) *
            </label>
            <input
              type="number"
              min="1"
              placeholder="Masukkan jumlah stok..."
              value={stock}
              onChange={(e) => setStock(e.target.value)}
              className="w-full p-3 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
              required
            />
          </div>

          {/* Type Readonly Indicator */}
          <div className="space-y-1.5">
            <label className="text-gray-700 font-bold uppercase text-[10px] tracking-wider">
              Arah Tipe Mutasi
            </label>
            <div className="p-3 rounded-2xl font-mono font-extrabold text-sm flex items-center justify-between border bg-emerald-50 text-emerald-800 border-emerald-200">
              <span>{type === "in" ? "STOK MASUK (IN)" : "STOK KELUAR (OUT)"}</span>
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-600 text-white">
                Otomatis
              </span>
            </div>
          </div>
        </div>

        {/* Message / Catatan */}
        <div className="space-y-1.5">
          <label className="text-gray-700 font-bold uppercase text-[10px] tracking-wider">
            Catatan / Alasan Mutasi
          </label>
          <textarea
            rows="3"
            placeholder="Tuliskan catatan atau keterangan mutasi stok obat ini..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            className="w-full p-3 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
          />
        </div>

        {/* Upload Bukti Evidence */}
        <div className="space-y-1.5">
          <label className="text-gray-700 font-bold uppercase text-[10px] tracking-wider flex items-center gap-1.5">
            <FaUpload className="text-emerald-600" /> Upload Lampiran / Foto Bukti Evidence
          </label>

          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleFileDrop}
            onClick={() => fileInputRef.current?.click()}
            className="border-2 border-dashed border-gray-200 rounded-3xl p-6 text-center cursor-pointer hover:border-emerald-400 hover:bg-emerald-50/30 transition-all space-y-2"
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) readFile(file);
              }}
            />
            {previewUrl ? (
              <div className="space-y-2">
                <img
                  src={previewUrl}
                  alt="Preview Bukti"
                  className="max-h-48 mx-auto rounded-2xl shadow-md object-contain border border-gray-200"
                />
                <p className="text-[10px] text-emerald-600 font-bold">
                  Klik untuk mengganti foto bukti
                </p>
              </div>
            ) : (
              <div className="space-y-1 text-gray-400">
                <FaUpload className="w-8 h-8 mx-auto text-emerald-600/50" />
                <p className="font-bold text-gray-700 text-xs">
                  Tarik foto ke sini, atau klik untuk memilih file
                </p>
                <p className="text-[10px] text-gray-400">Format gambar JPG, PNG, WEBP</p>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
          <button
            type="button"
            onClick={() => navigate("/rebalance")}
            className="px-5 py-2.5 rounded-2xl bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold transition-colors flex items-center gap-1.5"
          >
            <FaArrowLeft className="w-3.5 h-3.5" />
            <span>Batal</span>
          </button>

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            type="submit"
            disabled={loadingSubmit}
            className="px-6 py-3 rounded-2xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white text-xs font-bold shadow-lg shadow-emerald-600/20 transition-all flex items-center gap-2"
          >
            {loadingSubmit ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <FaCheck className="w-4 h-4" />
            )}
            <span>Simpan Data Rebalance</span>
          </motion.button>
        </div>
      </motion.form>
    </div>
  );
};

export default CreateRebalance;
